import React, {useState} from 'react';
import {
    IonButton,
    IonIcon,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonList,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonToggle, useIonActionSheet, useIonLoading, useIonToast,
    useIonViewWillEnter
} from '@ionic/react';
import {getIpAccesses, patchIpAccess} from "../repositories/ipAccessRepository";
import {useInfiniteQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import moment from "moment/moment";
import {refresh, hourglassOutline} from "ionicons/icons";

const IpAccessEntityList = ({userId, serviceId, readonly}) => {
    const [searchText, setSearchText] = useState('');
    const queryClient = useQueryClient();
    const [presentActionSheet] = useIonActionSheet();
    const [presentLoading, dismissLoading] = useIonLoading();
    const [presentToast] = useIonToast();

    const ipAccessesQuery = useInfiniteQuery({
        queryKey: ['ipAccesses', 'search', {userId, serviceId, searchText}],
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => lastPage.length > 0 ? pages.length + 1 : undefined,
        queryFn: ({pageParam}) => getIpAccesses(userId, serviceId, searchText, pageParam),
        refetchInterval: 5000,
    });

    useIonViewWillEnter(() => {
        ipAccessesQuery.refetch();
    })

    const reload = async (event) => {
        await ipAccessesQuery.refetch();
        event.detail.complete();
    };

    const loadMore = async (event) => {
        await ipAccessesQuery.fetchNextPage();
        event.target.complete();
    };

    const handleSearch = async (event) => {
        setSearchText(event.detail.value);
    };

    const selectDuration = () => {
        return new Promise((resolve, reject) => {
            presentActionSheet({
                header: 'Access duration',
                buttons: [
                    {
                        text: 'Forever',
                        role: 'destructive',
                        handler: () => resolve('forever'),
                    },
                    {
                        text: '1 minute',
                        handler: () => resolve([1, 'minutes']),
                    },
                    {
                        text: '5 minutes',
                        handler: () => resolve([5, 'minutes'])
                    },
                    {
                        text: '1 hour',
                        handler: () => resolve([1, 'hours'])
                    },
                    {
                        text: '1 day',
                        handler: () => resolve([1, 'days'])
                    },
                    {
                        text: '1 week',
                        handler: () => resolve([1, 'weeks'])
                    },
                    {
                        text: '1 month',
                        handler: () => resolve([1, 'months'])
                    },
                    {
                        text: 'Cancel',
                        role: 'cancel',
                        handler: () => resolve('cancel')
                    },
                ]
            });
        });
    }

    const addNewIpAddress = async () => {
        try {
            const duration = await selectDuration();
            if (duration === 'cancel') {
                return;
            }
            await presentLoading({message: 'Granting IP address access'})
            await patchIpAccess(userId, serviceId, searchText, [
                {op: 'replace', path: '/enabled', value: true},
                {
                    op: 'replace',
                    path: '/revokeAt',
                    value: duration === 'forever' ? null : moment().utc().add(...duration).toISOString()
                },
            ]);
            await dismissLoading();
            await presentToast({message: "Successfully granted IP address access", duration: 2000, color: "success"});
        } catch (e) {
            console.error(e);
            await dismissLoading();
            await presentToast({message: "Failed to grant IP address access", duration: 5000, color: "danger"});
            throw e;
        }
    }

    const addNewIpAddressMutation = useMutation({
        mutationFn: addNewIpAddress,

        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['ipAccesses', 'search', {userId, serviceId, searchText}] });
            const previousValue = queryClient.getQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}]);

            return { previousValue };
        },

        // On error, revert to previous value
        onError: (err, newValue, context) => {
            queryClient.setQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}], context?.previousValue);
        },

        // Always refetch after error or success
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['ipAccesses', 'search'] });
        },
    })

    const toggle = async (ipAccess) => {
        if (ipAccess.enabled) {
            try {
                await presentLoading({message: 'Revoking IP address access'})
                await patchIpAccess(userId, serviceId, ipAccess.ipAddress, [
                    {op: 'replace', path: '/enabled', value: false}
                ]);
                await dismissLoading();
                await presentToast({message: "Successfully revoked IP address access", duration: 2000, color: "success"});
            } catch (e) {
                await dismissLoading();
                const errorMessage = e.response?.status === 401
                    ? "Unauthorized: You don't have permission to revoke this IP address access"
                    : "Failed to revoke IP address access";
                await presentToast({message: errorMessage, duration: 5000, color: "danger"});
                throw e;
            }
        } else {
            try {
                const duration = await selectDuration();
                if (duration === 'cancel') {
                    return;
                }
                await presentLoading({message: 'Granting IP address access'})
                await patchIpAccess(userId, serviceId, ipAccess.ipAddress, [
                    {op: 'replace', path: '/enabled', value: true},
                    {
                        op: 'replace',
                        path: '/revokeAt',
                        value: duration === 'forever' ? null : moment().utc().add(...duration).toISOString()
                    },
                ]);
                await dismissLoading();
                await presentToast({message: "Successfully granted IP address access", duration: 2000, color: "success"});
            } catch (e) {
                console.error(e);
                await dismissLoading();
                await presentToast({message: "Failed to grant IP address access", duration: 5000, color: "danger"});
                throw e;
            }
        }
    }

    const toggleMutation = useMutation({
        mutationFn: toggle,

        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['ipAccesses', 'search', {userId, serviceId, searchText}] });
            const previousValue = queryClient.getQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}]);

            return { previousValue };
        },

        // On error, revert to previous value
        onError: (err, newValue, context) => {
            queryClient.setQueryData(['ipAccesses', 'search', {userId, serviceId, searchText}], context?.previousValue);
        },

        // Always refetch after error or success
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['ipAccesses', 'search'] });
        },
    })

    const isValidIPv4 = ip =>
        /^(\d{1,3}\.){3}\d{1,3}$/.test(ip) &&
        ip.split('.').every(n => n >= 0 && n <= 255);

    const getAddIpAddressButton = () => {
        if (readonly) {
            return <IonItem key='no matching ip addresses'>No matching IP addresses found.</IonItem>
        }

        if (!isValidIPv4(searchText)) {
            return <IonItem key='no matching ip addresses add new'>No matching IP addresses found. To add new, enter a valid IP address.</IonItem>
        }

        return <IonItem key='add new' button onClick={() => addNewIpAddressMutation.mutate()}>Add IP address '{searchText}'</IonItem>
    }

    const getExpiresString = revokeAt => {
        return revokeAt > moment.utc() ? 'Expires' : 'Should have been expired';
    }


    const renderExpiredTag = access => {
        if (!access.modifiedProgrammatically && access.revokeAt == null) return <></>
        return <p style={{
            display: 'flex', flexDirection: 'row',
            alignItems: 'center'
        }}>
            <IonIcon style={{marginRight: '5px'}} icon={hourglassOutline}></IonIcon>
            <span>{access.revokeAt == null
                ? `Expired ${moment.utc(access.revokedAt).fromNow()}`
                : `${getExpiresString(moment.utc(access.revokeAt))} ${moment.utc(access.revokeAt).fromNow()}`}</span>
        </p>
    }

    const ipAccesses = ipAccessesQuery.data?.pages.flatMap(data => data);

    return (
            <>
                <IonRefresher slot="fixed" onIonRefresh={reload}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>

                <IonItemDivider sticky className="access-list-search-bar">
                    <IonSearchbar
                        placeholder="Search or add new IP address"
                        debounce={1000}
                        mode="ios"
                        enterkeyhint="search" inputmode="search" onIonInput={(ev) => handleSearch(ev)}></IonSearchbar>
                    <IonButton fill="clear" slot="end" onClick={() => ipAccessesQuery.refetch()}>
                        <IonIcon icon={refresh} />
                    </IonButton>
                </IonItemDivider>

                <IonList>
                    {ipAccesses?.length === 0
                        ? getAddIpAddressButton()
                        : ipAccesses?.map(entity => (
                                <IonItem button key={entity.ipAddress} onClick={() => toggleMutation.mutate(entity)}>
                                    <IonLabel>
                                        <h3>{entity.ipAddress}</h3>
                                        {renderExpiredTag(entity)}
                                    </IonLabel>
                                    <IonToggle disabled={readonly} slot="end" checked={entity.enabled}></IonToggle>
                                </IonItem>
                            ))
                    }
                </IonList>

                <IonInfiniteScroll onIonInfinite={loadMore} threshold="100px" disabled={!ipAccessesQuery.hasNextPage}>
                    <IonInfiniteScrollContent loadingText="Loading more..."></IonInfiniteScrollContent>
                </IonInfiniteScroll>
            </>
    );
};

export default IpAccessEntityList;